"use client";
import { useEffect, useMemo, useState } from "react";
import Portal from "./globalPortal";
import { useDebounce, useLockBodyScroll } from "@uidotdev/usehooks";
import { cn } from "@/lib";
import { IBaseModalProps, IService, IServiceCard } from "@/interfaces";
import { OlCloseCircle } from "solar-icon-react/ol";
import { FlexBetween } from "./ui";
import AccordionMenu from "./accordionMenu";
import { getInquiriesAdapter } from "@/lib/adapters/getInquiriesAdapter";
import { useGetInquiries } from "@/hooks";
import { useTranslations } from "next-intl";
import SearchBar from "./ui/searchBar";
import Link from "next/link";

export function filterServicesByLabelFlat(
  coreServices: IServiceCard[],
  searchTerm: string,
): IService[] {
  return coreServices.flatMap((card) =>
    card.services.filter((service) =>
      service.label.toLowerCase().replace(/\s/g, "").includes(searchTerm),
    ),
  );
}

const Sidebar = ({ isOpen, onClose }: IBaseModalProps) => {
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const t = useTranslations();
  const { data: services } = useGetInquiries();
  const debouncedSearchQuery = useDebounce(query, 1000);
  useLockBodyScroll();

  const coreServicesData = services?.data
    ? getInquiriesAdapter(services.data)
    : [];

  const filteredServices: IService[] = useMemo(() => {
    const searchTerm = debouncedSearchQuery
      .trim()
      .toLowerCase()
      .replace(/\s/g, "");
    if (!searchTerm) return coreServicesData.flatMap((card) => card.services);
    return filterServicesByLabelFlat(coreServicesData, searchTerm);
  }, [debouncedSearchQuery, coreServicesData]);

  useEffect(() => {
    const searchTerm = debouncedSearchQuery
      .trim()
      .toLowerCase()
      .replace(/\s/g, "");
    setIsFiltered(!!searchTerm);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const closeSidebar = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  if (!services?.data) {
    return null;
  }

  return (
    <Portal>
      <div
        className={cn(
          "fixed inset-0 z-40 ms-auto transition-all duration-300",
          visible
            ? "visible w-[calc(100%-300px)] bg-black/30 lg:w-[calc(100%-400px)]"
            : "invisible w-full bg-black/0",
        )}
        onClick={closeSidebar}
      />
      <div
        className={cn(
          "p- fixed top-0 right-0 z-50 h-full w-[320px] overflow-hidden overflow-y-auto border-l border-white/18 bg-white p-8 backdrop-blur-md transition-transform duration-300 lg:w-[400px]",
          visible ? "translate-x-0" : "translate-x-full",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={"flex flex-col items-center justify-center gap-8"}>
          <FlexBetween className={"w-full"}>
            <h1
              className={cn("text-primary-main text-lg font-bold lg:text-xl")}
            >
              {t("services.title")}
            </h1>
            <button
              className={cn("flex cursor-pointer items-center justify-center")}
              onClick={closeSidebar}
            >
              <span className="max-h-[28px] max-w-[28px] lg:max-h-[35px] lg:max-w-[35px]">
                <OlCloseCircle className={cn("text-primary-main! size-8")} />
              </span>
            </button>
          </FlexBetween>

          <hr className={cn("border-primary-main w-full border-t-2")} />
        </header>
        <SearchBar
          className="mt-5 text-black"
          query={query}
          variant="default"
          setQuery={(val) => setQuery(val)}
        />
        <div className={cn("mt-12 flex flex-col gap-4 lg:gap-6")}>
          {isFiltered ? (
            filteredServices.length > 0 ? (
              <ul
                className={cn(
                  "ms-18 mt-4 flex w-full flex-col justify-center gap-4 lg:gap-6",
                )}
              >
                {filteredServices.map((services, index) => (
                  <li
                    key={index}
                    className={cn(
                      "text-custom-gray-600 hover:text-secondary-main text-sm transition-colors lg:text-base",
                      services.disabled === "unavailability" &&
                        "text-custom-gray-100 pointer-events-none",
                    )}
                  >
                    <Link
                      id={`sidebar-service-${services.flag}`}
                      title={services.label}
                      aria-disabled={services.disabled === "unavailability"}
                      href={
                        services.disabled !== "unavailability"
                          ? services.href
                          : "#"
                      }
                      onClick={closeSidebar}
                    >
                      {services.label}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-12 text-center text-sm text-gray-400 lg:text-base">
                هیچ سرویسی با این عنوان یافت نشد.
              </p>
            )
          ) : (
            <>
              {coreServicesData.map(
                ({ categoryIcon, category, services }, index) => (
                  <AccordionMenu
                    closeSidebar={closeSidebar}
                    key={index}
                    icon={categoryIcon}
                    category={t(`categories.${category}` as any)}
                    items={services}
                  />
                ),
              )}
            </>
          )}
        </div>
      </div>
    </Portal>
  );
};
export default Sidebar;
